import cloneDeep from 'lodash-es/cloneDeep'
import sortBy from 'lodash-es/sortBy'
import * as Yup from 'yup'

import type { DaySchedule } from '@b-stock/location-api-client'

type ValidateResult = {
  slotIndexes: number[]
  isValid: boolean
}

export const validateTimeslots = (timeslots: DaySchedule[]): ValidateResult => {
  const sortedTimeslot = sortBy(cloneDeep(timeslots), ['from'])

  const res: ValidateResult = {
    slotIndexes: [],
    isValid: true,
  }

  // `entries()` rather than an index loop so `noUncheckedIndexedAccess` doesn't
  // force a non-null assertion on every element read.
  for (const [i, slot] of sortedTimeslot.entries()) {
    const start = parseInt(slot.from)
    const end = parseInt(slot.to)
    const validDuration = start < end

    const nextSlot = sortedTimeslot[i + 1]
    const nextStart = nextSlot ? parseInt(nextSlot.from) : undefined

    if (!validDuration) {
      res.slotIndexes.push(i)
      res.isValid = false
    }

    if (validDuration && nextStart && end > nextStart) {
      res.slotIndexes.push(i)
      res.isValid = false
    }
  }

  return res
}

Yup.addMethod(Yup.array, 'timeslot', function (fieldName: string) {
  const valueTest = (
    value: DaySchedule[] | undefined,
    ctx: Yup.TestContext
  ): boolean | Yup.ValidationError => {
    if (!value) {
      return true
    }

    const validationResult = validateTimeslots(value)
    if (!validationResult.isValid) {
      return ctx.createError({
        message: validationResult.slotIndexes.join('.'),
      })
    }
    return true
  }

  return this.when([fieldName], {
    is: (checked: boolean) => checked,
    then: () => this.test(valueTest),
    otherwise: () => this.optional().nullable(),
  })
})

declare module 'yup' {
  interface ArraySchema<
    // This needs to match Yup's own ArraySchema signature
    // oxlint-disable-next-line typescript-eslint/no-explicit-any
    TIn extends any[] | null | undefined,
    TContext,
    TDefault = undefined,
    TFlags extends Yup.Flags = '',
    T = unknown,
    C = unknown,
    TOut = unknown,
  > {
    timeslot(
      fieldName: string,
      errorMesage: string
    ): Yup.ArraySchema<TIn, TContext, TDefault, TFlags, T, C, TOut>
  }
}
